import type { ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { BrandMark } from "./brand-mark";

/**
 * Full-viewport branded loading state shared by the auth bootstrap
 * (`ProtectedRoute`) and the post-sign-in callback. A softly breathing halo
 * behind the brand mark plus a staggered three-dot indicator keeps the wait
 * feeling alive while staying clean and minimal. Honours `prefers-reduced-
 * motion`: the halo and dots fall back to a static, legible state.
 */
export function LoadingScreen({
  message = "Getting everything ready…",
}: {
  message?: string;
}): ReactNode {
  const reduceMotion = useReducedMotion();

  return (
    <div className="grid min-h-screen place-items-center px-5 text-center">
      <div role="status" aria-label="Getting Tailspend ready">
        <div className="relative grid place-items-center">
          {/* Breathing halo. Sits behind the mark, never intercepts pointer. */}
          <motion.span
            aria-hidden="true"
            className="pointer-events-none absolute size-24 rounded-full bg-primary/15 blur-xl"
            initial={false}
            animate={
              reduceMotion
                ? { opacity: 0.2, scale: 1 }
                : { opacity: [0.12, 0.3, 0.12], scale: [0.9, 1.15, 0.9] }
            }
            transition={
              reduceMotion
                ? undefined
                : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
            }
          />
          <BrandMark className="size-16" />
        </div>
        <p className="mt-5 text-headline-md text-primary">Tailspend</p>
        <div
          className="mt-4 flex items-center justify-center gap-2"
          aria-hidden="true"
        >
          {[0, 1, 2].map((index) => (
            <motion.span
              key={index}
              className="size-2 rounded-full bg-primary"
              initial={false}
              animate={
                reduceMotion
                  ? { opacity: 0.6 }
                  : { opacity: [0.35, 1, 0.35], scale: [0.7, 1, 0.7] }
              }
              transition={
                reduceMotion
                  ? undefined
                  : {
                      duration: 1.1,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: index * 0.18,
                    }
              }
            />
          ))}
        </div>
        <p className="mt-4 text-body-md text-on-surface-variant">{message}</p>
      </div>
    </div>
  );
}
